import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit2, MapPin, Users, Calendar, Search, Map } from 'lucide-react';
import { primaryButton, ghostButton, inputStyle, glass, glassLight, T, Badge, StatCard, EmptyState, Field } from '../ui';
interface Cliente {
  id: string;
  codigo: string;
  nombre: string;
  ubicacion: string;
  telefono: string;
}
interface RutaCliente {
  clienteId: string;
  clienteNombre: string;
  codigo: string;
  ubicacion: string;
  estado: 'pendiente' | 'visitado' | 'sin_compra';
}
interface Ruta {
  id: string;
  nombre: string;
  zona: string;
  supervisor: string;
  clientes: RutaCliente[];
  fechaCreacion: string;
  diasSemana: string[];
  estado: 'activa' | 'inactiva';
}
interface RutasProps {
  rutas: Ruta[];
  clientes: Cliente[];
  onAddRuta: (ruta: Ruta) => void;
  onDeleteRuta: (id: string) => void;
  onUpdateRuta: (ruta: Ruta) => void;
}
const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const visitaColor = (estado: RutaCliente['estado']) => {
  if (estado === 'visitado') return {
    color: T.ok,
    soft: T.okSoft
  };
  if (estado === 'sin_compra') return {
    color: T.warn,
    soft: T.warnSoft
  };
  return {
    color: T.textMuted,
    soft: T.surfaceSubtle
  };
};
const iconBtn: React.CSSProperties = {
  width: 30,
  height: 30,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: T.surface,
  cursor: 'pointer'
};
export function Rutas({
  rutas,
  clientes,
  onAddRuta,
  onDeleteRuta,
  onUpdateRuta
}: RutasProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState<'todas' | 'activa' | 'inactiva'>('activa');
  const [formData, setFormData] = useState<Partial<Ruta>>({
    estado: 'activa',
    clientes: [],
    diasSemana: []
  });
  const [selectedClientes, setSelectedClientes] = useState<string[]>([]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nombre || !formData.zona || !formData.supervisor) {
      alert('Por favor completa los campos obligatorios');
      return;
    }
    const rutaClientes: RutaCliente[] = selectedClientes.map(clienteId => {
      const cliente = clientes.find(c => c.id === clienteId);
      return {
        clienteId,
        clienteNombre: cliente?.nombre || '',
        codigo: cliente?.codigo || '',
        ubicacion: cliente?.ubicacion || '',
        estado: 'pendiente'
      };
    });
    if (editingId) {
      onUpdateRuta({
        ...formData,
        id: editingId,
        clientes: rutaClientes
      } as Ruta);
      setEditingId(null);
    } else {
      onAddRuta({
        ...formData,
        id: Date.now().toString(),
        clientes: rutaClientes,
        fechaCreacion: new Date().toISOString().split('T')[0]
      } as Ruta);
    }
    setFormData({
      estado: 'activa',
      clientes: [],
      diasSemana: []
    });
    setSelectedClientes([]);
    setShowForm(false);
  };
  const handleEdit = (ruta: Ruta) => {
    setFormData(ruta);
    setSelectedClientes(ruta.clientes.map(c => c.clienteId));
    setEditingId(ruta.id);
    setShowForm(true);
  };
  const toggleCliente = (clienteId: string) => {
    setSelectedClientes(prev => prev.includes(clienteId) ? prev.filter(id => id !== clienteId) : [...prev, clienteId]);
  };
  const toggleDia = (dia: string) => {
    setFormData(prev => ({
      ...prev,
      diasSemana: prev.diasSemana?.includes(dia) ? prev.diasSemana.filter(d => d !== dia) : [...(prev.diasSemana || []), dia]
    }));
  };
  const filteredRutas = rutas.filter(r => {
    const matchSearch = r.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || r.zona.toLowerCase().includes(searchTerm.toLowerCase()) || r.supervisor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filterEstado === 'todas' || r.estado === filterEstado;
    return matchSearch && matchEstado;
  });
  const rutasActivas = rutas.filter(r => r.estado === 'activa').length;
  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  }} data-id="element-972" data-mp="1dlhmyxrcrefz">
      {/* Header Stats */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 14
    }} data-id="element-973" data-mp="18npxeae6k4ey">
        
        <StatCard label="Total rutas" value={String(rutas.length)} note="Registradas en el sistema" accent={T.info} data-id="element-974" data-mp="fyra6yv7ac2s" />
        
        <StatCard label="Activas" value={String(rutasActivas)} note="En operación" accent={T.ok} data-id="element-975" data-mp="unx7uiee3u8k" />
        
        <StatCard label="Total clientes" value={String(rutas.reduce((sum, r) => sum + r.clientes.length, 0))} note="Asignados a rutas" accent={T.warn} data-id="element-976" data-mp="pvncgmuia1yw" />
        
      </div>

      {/* Controles */}
      <div style={{
      ...glassLight,
      padding: 16
    }} data-id="element-977" data-mp="3tccfhyuvqtjt">
        <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        alignItems: 'center'
      }} data-id="element-978" data-mp="28ul6h0ssmnll">
          
          <div style={{
          flex: '1 1 240px',
          position: 'relative'
        }} data-id="element-979" data-mp="2dneebxjk6c1w">
            <Search size={16} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-980" data-mp="1zvztdl63c0s7" />
            
            <input type="text" placeholder="Buscar ruta, zona o supervisor..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{
            ...inputStyle,
            paddingLeft: 36
          }} data-id="element-981" data-mp="2rji5fhz4y4a5" />
            
          </div>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as any)} style={{
          ...inputStyle,
          width: 'auto'
        }} data-id="element-982" data-mp="2niy565j9mkjs">
            
            <option value="todas" data-id="element-983" data-mp="1c5djrpgfu8g3">Todas</option>
            <option value="activa" data-id="element-984" data-mp="1c6f4kln9a9b0">Activas</option>
            <option value="inactiva" data-id="element-985" data-mp="3dujeyfyv7agc">Inactivas</option>
          </select>
          <button onClick={() => {
          setShowForm(!showForm);
          setEditingId(null);
          setFormData({
            estado: 'activa',
            clientes: [],
            diasSemana: []
          });
          setSelectedClientes([]);
        }} style={{
          ...primaryButton,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6
        }} data-id="element-986" data-mp="3dvuhkcgkqc5v">
            
            <Plus size={15} data-id="element-987" data-mp="2q4ff8josruum" /> Nueva Ruta
          </button>
        </div>
      </div>

      {/* Formulario */}
      <AnimatePresence data-id="element-988" data-mp="2l52tk29nwttm">
        {showForm && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} style={{
        overflow: 'hidden'
      }} data-id="element-989" data-mp="2l643g3exza9r">
          
            <div style={{
          ...glass,
          padding: 20
        }} data-id="element-990" data-mp="fdp6bhhh5ql8">
              <h3 style={{
            fontSize: 15,
            fontWeight: 800,
            marginBottom: 16,
            color: T.text
          }} data-id="element-991" data-mp="1x8y174i0um8b">
              
                {editingId ? 'Editar Ruta' : 'Nueva Ruta'}
              </h3>
              <form onSubmit={handleSubmit} data-id="element-992" data-mp="271wzxf78ft5n">
                <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 14,
              marginBottom: 18
            }} data-id="element-993" data-mp="2b1np5eh42182">
                
                  <Field label="Nombre de ruta *" data-id="element-994" data-mp="1j9g6y9lyigrk">
                    <input type="text" value={formData.nombre || ''} onChange={e => setFormData({
                  ...formData,
                  nombre: e.target.value
                })} style={inputStyle} placeholder="Ruta Centro" data-id="element-995" data-mp="1jcc5o05mp9h2" />
                  
                  </Field>
                  <Field label="Zona *" data-id="element-996" data-mp="2yqgm9e9gyf49">
                    <input type="text" value={formData.zona || ''} onChange={e => setFormData({
                  ...formData,
                  zona: e.target.value
                })} style={inputStyle} placeholder="Centro / Este / Norte" data-id="element-997" data-mp="33q33rzwbyhiv" />
                  
                  </Field>
                  <Field label="Supervisor *" data-id="element-998" data-mp="2aclgiofb4u8v">
                    <input type="text" value={formData.supervisor || ''} onChange={e => setFormData({
                  ...formData,
                  supervisor: e.target.value
                })} style={inputStyle} placeholder="Nombre del supervisor" data-id="element-999" data-mp="2o6uu3flxzwn2" />
                  
                  </Field>
                  <Field label="Estado" data-id="element-1000" data-mp="2t3urz38n1t33">
                    <select value={formData.estado || 'activa'} onChange={e => setFormData({
                  ...formData,
                  estado: e.target.value as any
                })} style={inputStyle} data-id="element-1001" data-mp="2t70j0o0tv51g">
                    
                      <option value="activa" data-id="element-1002" data-mp="1cq4j7j6kqgn4">Activa</option>
                      <option value="inactiva" data-id="element-1003" data-mp="1csqspod0z67z">Inactiva</option>
                    </select>
                  </Field>
                </div>

                <Field label="Días de operación" data-id="element-1004" data-mp="ykm5odetsvjg">
                  <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 8
              }} data-id="element-1005" data-mp="ylxhn0ifd9ln">
                  
                    {DIAS_SEMANA.map(dia => {
                  const checked = formData.diasSemana?.includes(dia) || false;
                  return <label key={dia} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    padding: '7px 10px',
                    borderRadius: 8,
                    border: `1px solid ${checked ? T.accentBorder : T.border}`,
                    background: checked ? T.accentSoft : T.surface
                  }} data-id="element-1006" data-mp="tsb7pg3nbjtj">
                        
                          <input type="checkbox" checked={checked} onChange={() => toggleDia(dia)} style={{
                      width: 15,
                      height: 15,
                      accentColor: T.accent
                    }} data-id="element-1007" data-mp="20bst0qvjdti7" />
                        
                          <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: checked ? T.accent : T.textSecondary
                    }} data-id="element-1008" data-mp="2uos0i98ahq0z">
                          
                            {dia}
                          </span>
                        </label>;
                })}
                  </div>
                </Field>

                <Field label={`Clientes en esta ruta (${selectedClientes.length} seleccionados)`} data-id="element-1009" data-mp="3dnmhlov872kb">
                  <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 6,
                maxHeight: 260,
                overflowY: 'auto',
                background: T.surfaceSubtle,
                border: `1px solid ${T.border}`,
                borderRadius: 10,
                padding: 10
              }} data-id="element-1010" data-mp="2znuzpd1l9211">
                  
                    {clientes.length === 0 ? <p style={{
                  gridColumn: '1 / -1',
                  color: T.textMuted,
                  fontSize: 13,
                  margin: 0
                }} data-id="element-1011" data-mp="1kp7eejih9kww">
                    
                        No hay clientes disponibles
                      </p> : clientes.map(cliente => {
                  const checked = selectedClientes.includes(cliente.id);
                  return <label key={cliente.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    padding: '7px 9px',
                    borderRadius: 8,
                    background: checked ? T.accentSoft : 'transparent'
                  }} data-id="element-1012" data-mp="120sq43zjokzs">
                        
                            <input type="checkbox" checked={checked} onChange={() => toggleCliente(cliente.id)} style={{
                      width: 15,
                      height: 15,
                      accentColor: T.accent
                    }} data-id="element-1013" data-mp="j883v5ecpe4y" />
                        

                            <span style={{
                      fontSize: 13,
                      color: checked ? T.accent : T.textSecondary,
                      fontWeight: checked ? 700 : 500
                    }} data-id="element-1014" data-mp="2japqqkxeslz3">
                          
                              {cliente.nombre}
                            </span>
                          </label>;
                })}
                  </div>
                </Field>

                <div style={{
              display: 'flex',
              gap: 8,
              marginTop: 16
            }} data-id="element-1015" data-mp="20d6qo6sxidot">
                  <button type="submit" style={primaryButton} data-id="element-1016" data-mp="24gcr2xtd1and">
                    {editingId ? 'Actualizar' : 'Crear'} ruta
                  </button>
                  <button type="button" onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setSelectedClientes([]);
              }} style={ghostButton} data-id="element-1017" data-mp="3fsdmuwon5zcm">
                  
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Lista de Rutas */}
      {filteredRutas.length === 0 ? <div style={{
      ...glass,
      padding: 20
    }} data-id="element-1018" data-mp="3l4j4crn142pz">
          <EmptyState icon={<Map size={20} data-id="element-1020" data-mp="3h4s3tw7pa889" />} title="No hay rutas disponibles" hint="Crea una ruta nueva o ajusta los filtros de búsqueda." data-id="element-1019" data-mp="3h48xzdvbraal" />
        
        </div> : <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 14
    }} data-id="element-1021" data-mp="37745yxessklv">
          {filteredRutas.map(ruta => <div key={ruta.id} style={{
        ...glass,
        padding: 20
      }} data-id="element-1022" data-mp="328sxaq08og58">
              <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14
        }} data-id="element-1023" data-mp="2y6xxjpxyiatk">
          
                <div data-id="element-1024" data-mp="wfb1h9z7jpq8">
                  <h3 style={{
              fontSize: 16,
              fontWeight: 800,
              color: T.text,
              margin: '0 0 8px'
            }} data-id="element-1025" data-mp="wcozdpv362fe">
              
                    {ruta.nombre}
                  </h3>
                  <div style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              fontSize: 12.5,
              color: T.textSecondary
            }} data-id="element-1026" data-mp="uhuo1e4r5xn2">
              
                    <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }} data-id="element-1027" data-mp="kqgox7gpqzug">
                
                      <MapPin size={13} data-id="element-1028" data-mp="atlt4wbtrdjv" /> {ruta.zona}
                    </span>
                    <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }} data-id="element-1029" data-mp="arhepkgauw5c">
                
                      <Users size={13} data-id="element-1030" data-mp="1tytog2nohtg" /> Supervisor: {ruta.supervisor}
                    </span>
                    <span style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5
              }} data-id="element-1031" data-mp="6qfh18216s5h">
                
                      <Calendar size={13} data-id="element-1032" data-mp="3ecrp9bma4jxb" />{' '}
                      {ruta.diasSemana?.join(', ') || 'Sin días asignados'}
                    </span>
                  </div>
                </div>
                <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }} data-id="element-1033" data-mp="3oao1k6qydkx0">
                  <Badge label={ruta.estado} color={ruta.estado === 'activa' ? T.ok : T.danger} soft={ruta.estado === 'activa' ? T.okSoft : T.dangerSoft} data-id="element-1034" data-mp="63rj6nvvbqfm" />
              
                  <button onClick={() => handleEdit(ruta)} title="Editar" style={{
              ...iconBtn,
              color: T.info
            }} data-id="element-1035" data-mp="fucmgomhlj4v">
              
                    <Edit2 size={14} data-id="element-1036" data-mp="axm7k4mf4tl1" />
                  </button>
                  <button onClick={() => onDeleteRuta(ruta.id)} title="Eliminar" style={{
              ...iconBtn,
              color: T.danger
            }} data-id="element-1037" data-mp="ow2hs5jhp27x">
              
                    <Trash2 size={14} data-id="element-1038" data-mp="3doyz6ui4swdb" />
                  </button>
                </div>
              </div>

              <div style={{
          borderTop: `1px solid ${T.border}`,
          paddingTop: 14
        }} data-id="element-1039" data-mp="3iobwk96mplu5">
                <h4 style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: T.textMuted,
            margin: '0 0 10px'
          }} data-id="element-1040" data-mp="3mo2inzl7ndsc">
            
                  Clientes asignados ({ruta.clientes.length})
                </h4>
                {ruta.clientes.length === 0 ? <p style={{
            fontSize: 13,
            color: T.textMuted,
            margin: 0
          }} data-id="element-1041" data-mp="a6xjy0ac4jd4">
                    Sin clientes asignados
                  </p> : <div style={{
            overflowX: 'auto'
          }} data-id="element-1042" data-mp="e6pugijagzl4">
                    <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12.5
            }} data-id="element-1043" data-mp="2c0n3asiuqjhh">
              
                      <thead data-id="element-1044" data-mp="28hy16vomv4sa">
                        <tr data-id="element-1045" data-mp="28i7g9o853rj7">
                          {['Código', 'Nombre', 'Ubicación', 'Estado visita'].map(h => <th key={h} style={{
                    textAlign: 'left',
                    padding: '0 8px 8px',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: T.textMuted,
                    borderBottom: `1px solid ${T.border}`
                  }} data-id="element-1046" data-mp="23omtksbzyjzb">
                      
                              {h}
                            </th>)}
                        </tr>
                      </thead>
                      <tbody data-id="element-1047" data-mp="2dgiu0a1k2m6p">
                        {ruta.clientes.map((cliente, idx) => {
                  const vc = visitaColor(cliente.estado);
                  return <tr key={idx} data-id="element-1048" data-mp="16xu8fga7i1qo" data-mpi={idx}>
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`,
                      fontFamily: 'monospace',
                      color: T.info,
                      fontWeight: 700
                    }} data-id="element-1049" data-mp="120atee5opkxo">
                        
                                {cliente.codigo}
                              </td>
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`,
                      color: T.text
                    }} data-id="element-1050" data-mp="2x3h1gq7xrxcv">
                        
                                {cliente.clienteNombre}
                              </td>
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`,
                      color: T.textSecondary
                    }} data-id="element-1051" data-mp="3b2qitt7voxif">
                        
                                {cliente.ubicacion}
                              </td>
                              <td style={{
                      padding: '8px',
                      borderBottom: `1px solid ${T.border}`
                    }} data-id="element-1052" data-mp="cfckocsracms">
                        
                                <Badge label={cliente.estado} color={vc.color} soft={vc.soft} dot={false} data-id="element-1053" data-mp="1s4kgwg3zbrh9" />
                        
                              </td>
                            </tr>;
                })}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </div>)}
        </div>}
    </div>;
}